// lib/form-error-utils.ts or utils/form-error-utils.ts
import { FieldValues, Path, UseFormSetError } from "react-hook-form";

import { ApiError } from "@/lib/api";

/**
 * Maps API error field keys to form field names
 * Override this function for specific forms if needed
 */
export const mapFieldError = <T extends FieldValues>(key: string): Path<T> | "root" => {
  if (key === "non_field_errors" || key === "recaptcha") return "root";
  return key as Path<T>;
};

/**
 * Handles API errors and sets appropriate form errors
 * @param error - The ApiError instance
 * @param setError - React Hook Form's setError function
 * @param customFieldMapper - Optional custom field mapping function
 */
export const handleApiError = <T extends FieldValues>(
  error: ApiError,
  setError: UseFormSetError<T>,
  customFieldMapper?: (key: string) => Path<T> | "root",
) => {
  const fieldMapper = customFieldMapper || mapFieldError<T>;

  // Check if we have error data to process
  if (error.data && error.status === 400) {
    // Convert the data to the expected format and process each field error
    const errorData = error.data as Record<string, string[]>;

    Object.entries(errorData).forEach(([key, messages]) => {
      const field = fieldMapper(key);
      setError(field, {
        type: "manual",
        message: Array.isArray(messages) ? messages[0] : (messages as string),
      });
    });
  } else {
    // Handle cases where there's no structured error data
    setError("root", {
      type: "manual",
      message: error.message || "An error occurred during form submission.",
    });
  }
};

/**
 * Creates a handleApiError function bound to a specific form's setError
 * Useful for consistent error handling across multiple API calls in the same component
 */
export const createApiErrorHandler = <T extends FieldValues>(
  setError: UseFormSetError<T>,
  customFieldMapper?: (key: string) => Path<T> | "root",
) => {
  return (error: ApiError) => handleApiError(error, setError, customFieldMapper);
};
